import { Cartesian } from "@examspark/types";

import { exists } from "./misc";

export const constrainDimensionsByWidth = (
  width: number,
  height: number,
  desiredWidth: number,
) => {
  const scale = desiredWidth / width;
  if (width > desiredWidth) {
    // return height
    const _height = height * scale;
    // console.log('scale', height, width, desiredWidth, _height);

    return {
      scale: scale,
      width: desiredWidth,
      height: _height,
    };
  }

  return {
    scale: 1,
    width,
    height,
  };
};

// Given the two points of a line, calculate 2 coordinates that follow that line
// at the edge of the graph. These let us draw a neat line with arrows
export const calculateLineCoordinates = (props: {
  shape: Cartesian.Shape.Line | Cartesian.Shape.DashedLine;
  x: Cartesian.Axis;
  y: Cartesian.Axis;
}): [Cartesian.Coordinate, Cartesian.Coordinate] | undefined => {
  const shape = props.shape;

  const slope =
    (shape.coords[1].y - shape.coords[0].y) /
    (shape.coords[1].x - shape.coords[0].x);

  // 0/0 is undefined. which means the 2 points
  // are the same. Which means we can't draw a line
  // but not worth throwing an error below
  if (Number.isNaN(slope)) {
    return undefined;
  }

  // vertical line
  if (slope === Infinity || slope === -Infinity) {
    return [
      {
        x: shape.coords[0].x,
        y: props.y.min,
      },
      {
        x: shape.coords[0].x,
        y: props.y.max,
      },
    ];
  }

  const xMinIntercept = {
    x: props.x.min,
    y: slope * props.x.min + shape.coords[0].y - slope * shape.coords[0].x,
  };
  const xMaxIntercept = {
    x: props.x.max,
    y: slope * props.x.max + shape.coords[0].y - slope * shape.coords[0].x,
  };
  const yMinIntercept = {
    x: (props.y.min - shape.coords[0].y + slope * shape.coords[0].x) / slope,
    y: props.y.min,
  };
  const yMaxIntercept = {
    x: (props.y.max - shape.coords[0].y + slope * shape.coords[0].x) / slope,
    y: props.y.max,
  };

  // handle epsilon
  // 1% error should be good enough for any rounding errors
  const eX = Math.abs((props.x.max - props.x.min) / 100);
  const eY = Math.abs((props.y.max - props.y.min) / 100);

  // of those 4 points, find the 2 within the bounds of the graph
  const coords = [
    xMinIntercept,
    xMaxIntercept,
    yMinIntercept,
    yMaxIntercept,
  ].filter((coord) => {
    return (
      coord.x >= props.x.min - eX &&
      coord.x <= props.x.max + eX &&
      coord.y >= props.y.min - eY &&
      coord.y <= props.y.max + eY
    );
  });
  // if the intercepts are close to the corner of the graph, we may still have
  //  too many due to our epsilon. The easiest way to find the "right" points
  //  seems to be to find the two that are closest to the center of the graph
  if (coords.length !== 2) {
    const centerCoord = {
      x: (props.x.min + props.x.max) / 2,
      y: (props.y.min + props.y.max) / 2,
    };

    coords.sort((a, b) => {
      const distA = Math.sqrt(
        Math.pow(a.x - centerCoord.x, 2) + Math.pow(a.y - centerCoord.y, 2),
      );
      const distB = Math.sqrt(
        Math.pow(b.x - centerCoord.x, 2) + Math.pow(b.y - centerCoord.y, 2),
      );
      return distA - distB;
    });
  }

  return [coords[0], coords[1]];
};

// This really hurt my brain to write. There's probably a better way to do this
export const calculateRayCoordinates = (props: {
  shape: Cartesian.Shape.Ray;
  x: Cartesian.Axis;
  y: Cartesian.Axis;
}): [Cartesian.Coordinate, Cartesian.Coordinate] | undefined => {
  const [start, end] = props.shape.coords;

  // define the ray direction (this is rise and run, aka the 2 parts of the slope)
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // compute the intersection of the ray with the graph bounds
  const intersection = [
    // these are distance (time) traveled until the ray intersects the graph bounds
    (props.x.min - start.x) / dx, // left
    (props.x.max - start.x) / dx, // right
    (props.y.min - start.y) / dy, // bottom
    (props.y.max - start.y) / dy, // top
  ]
    .map((t) => {
      // if t (time) is negative, the intersection point is behind the start point
      if (t <= 0) return undefined;

      const x = start.x + t * dx;
      const y = start.y + t * dy;

      // if the intersection point is inside the graph bounds
      if (
        x >= props.x.min &&
        x <= props.x.max &&
        y >= props.y.min &&
        y <= props.y.max
      ) {
        return { x, y, t };
      }

      return undefined;
    })
    .filter(exists)
    // find the intersection point with the minimum t value (shortest distance)
    .reduce(
      (closestIntersection, intersectionPoint) => {
        if (!intersectionPoint) return closestIntersection;
        if (intersectionPoint.t < closestIntersection.t) {
          return intersectionPoint;
        }
        return closestIntersection;
      },
      {
        x: 0,
        y: 0,
        t: Infinity,
      },
    );

  // if our reduction didn't find any intersections, return undefined
  if (intersection.t === Infinity) {
    return undefined;
  }

  const { x, y } = intersection;

  return [start, { x, y }];
};
